import type { AccessSummary } from "./effective-access";
export type AuthState = {
  isLoaded: boolean;
  isSignedIn: boolean;
};

export type UserData = AccessSummary & {
  needsOnboarding?: boolean;
  [key: string]: unknown;
};

export type UserState = {
  user: UserData | null | undefined;
  isLoading: boolean;
  isError: boolean;
  errorStatus?: number;
};

export type RedirectDecision =
  | { action: "loading" }
  | { action: "redirect"; to: string }
  | { action: "render" }
  | { action: "error" };

export type SignInAudience = "member" | "admin" | "coach";

export type AdminSignInDecision =
  | { action: "loading" }
  | { action: "redirect"; to: string }
  | { action: "render-sign-in" }
  | { action: "error" }
  | { action: "switch-account" };

export type CoachSignInDecision =
  | { action: "loading" }
  | { action: "redirect"; to: string }
  | { action: "render-sign-in" }
  | { action: "wrong-role" }
  | { action: "blocked"; accessStatus?: any }
  | { action: "switch-account" };

const SIGN_IN_DESTINATIONS: Record<SignInAudience, string> = {
  member: "/dashboard",
  admin: "/admin",
  coach: "/coach/sign-in",
};

const RESERVED_SIGN_IN_PREFIXES = [
  "/sign-in",
  "/sign-up",
  "/review",
];

export function normalizeSignInDestination(value: string | null | undefined): string {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.length > 2048) {
    return SIGN_IN_DESTINATIONS.member;
  }
  try {
    const parsed = new URL(value, "https://plate-os.invalid");
    if (parsed.origin !== "https://plate-os.invalid") return SIGN_IN_DESTINATIONS.member;
    const normalized = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    if (
      parsed.pathname === SIGN_IN_DESTINATIONS.admin ||
      parsed.pathname === SIGN_IN_DESTINATIONS.coach ||
      parsed.pathname === "/onboarding"
    ) {
      return normalized;
    }
    if (
      parsed.pathname === "/" ||
      parsed.pathname.startsWith("/admin/") ||
      parsed.pathname === "/coach-ai" ||
      parsed.pathname.startsWith("/coach/") ||
      RESERVED_SIGN_IN_PREFIXES.some(
        (prefix) => parsed.pathname === prefix || parsed.pathname.startsWith(`${prefix}/`),
      )
    ) {
      return SIGN_IN_DESTINATIONS.member;
    }
    return normalized;
  } catch {
    return SIGN_IN_DESTINATIONS.member;
  }
}

export function getMemberSignInPath(returnTo: string): string {
  return `/sign-in?redirect_url=${encodeURIComponent(normalizeSignInDestination(returnTo))}`;
}

export function resolveHomeRedirect(auth: AuthState): RedirectDecision {
  if (!auth.isLoaded) return { action: "loading" };
  if (auth.isSignedIn) return { action: "redirect", to: "/dashboard" };
  return { action: "redirect", to: "/sign-in" };
}

export function resolveOnboardingAccess(auth: AuthState): RedirectDecision {
  if (!auth.isLoaded) return { action: "loading" };
  if (!auth.isSignedIn) return { action: "redirect", to: getOnboardingSignInPath() };
  return { action: "render" };
}

export function getOnboardingCompletionDestination(search = ""): string {
  return new URLSearchParams(search).get("acquisition_return") === "health-map" ? "/health-map" : "/dashboard";
}

export function getOnboardingSignInPath(search = ""): string {
  const path = getOnboardingCompletionDestination(search) === "/health-map" ? "/onboarding?acquisition_return=health-map" : "/onboarding";
  return `/sign-in?redirect_url=${encodeURIComponent(path)}`;
}

export function getRoleSignInDestination(audience: SignInAudience): string {
  return SIGN_IN_DESTINATIONS[audience];
}

export function getRoleSignInPath(audience: SignInAudience): string {
  if (audience === "coach") return SIGN_IN_DESTINATIONS.coach;
  return `/sign-in?redirect_url=${encodeURIComponent(getRoleSignInDestination(audience))}`;
}

export function getAdminGoogleAccountChoicePath(): string {
  return `${getRoleSignInPath("admin")}&choose_google=1`;
}

export function getAdminGoogleRedirectOptions(basePath: string) {
  return {
    strategy: "oauth_google" as const,
    redirectUrl: `${basePath}/sign-in/sso-callback`,
    redirectUrlComplete: `${basePath}/admin`,
    oidcPrompt: "select_account",
  };
}

export function resolveSignInDestination(location: string): string {
  const queryIndex = location.indexOf("?");
  if (queryIndex === -1) return SIGN_IN_DESTINATIONS.member;

  const requested = new URLSearchParams(location.slice(queryIndex + 1)).get("redirect_url");
  return normalizeSignInDestination(requested);
}

export function getSignInAudience(destination: string): SignInAudience {
  if (destination === SIGN_IN_DESTINATIONS.admin) return "admin";
  if (destination === SIGN_IN_DESTINATIONS.coach) return "coach";
  return "member";
}

export function resolveAdminSignInSession(
  audience: SignInAudience,
  auth: AuthState,
  userState: UserState,
): AdminSignInDecision {
  if (audience !== "admin") return { action: "render-sign-in" };
  if (!auth.isLoaded || userState.isLoading) {
    return { action: "loading" };
  }
  if (userState.user?.role === "admin") {
    return { action: "redirect", to: SIGN_IN_DESTINATIONS.admin };
  }
  if (auth.isSignedIn && userState.isError) {
    return { action: "error" };
  }
  if (userState.user || auth.isSignedIn) {
    return { action: "switch-account" };
  }
  return { action: "render-sign-in" };
}

export function resolveCoachSignInSession(
  audience: SignInAudience,
  auth: AuthState,
  userState: UserState,
  accessStatusState: { status?: any; isLoading: boolean; isError: boolean },
): CoachSignInDecision {
  if (audience !== "coach") return { action: "render-sign-in" };
  if (!auth.isLoaded) {
    return { action: "loading" };
  }

  if (!auth.isSignedIn) {
    return { action: "render-sign-in" };
  }

  if (userState.isLoading) {
    return { action: "loading" };
  }

  // A signed-in identity whose application user cannot be verified must never
  // fall through to Clerk or render managed data.
  if (userState.isError || !userState.user || typeof userState.user.role !== "string") {
    return { action: "blocked" };
  }

  if (userState.user.role !== "coach") {
    return { action: "wrong-role" };
  }

  if (accessStatusState.isLoading) {
    return { action: "loading" };
  }

  if (
    accessStatusState.isError ||
    !accessStatusState.status ||
    accessStatusState.status.state === "blocked" ||
    accessStatusState.status.state === "unresolved"
  ) {
    return { action: "blocked", accessStatus: accessStatusState.status };
  }

  // If full or read_only_delinquent, they can go to dashboard
  if (accessStatusState.status?.state === "full" || accessStatusState.status?.state === "read_only_delinquent") {
    return { action: "redirect", to: "/coach/dashboard" };
  }

  return { action: "blocked", accessStatus: accessStatusState.status };
}

export function resolveProtectedRoute(
  auth: AuthState,
  userState: UserState,
  returnTo = SIGN_IN_DESTINATIONS.member,
): RedirectDecision {
  const onboardingDestination = returnTo === "/health-map" ? "/onboarding?acquisition_return=health-map" : "/onboarding";
  if (!auth.isLoaded || (auth.isSignedIn && userState.isLoading)) {
    return { action: "loading" };
  }

  if (!auth.isSignedIn) {
    return { action: "redirect", to: getMemberSignInPath(returnTo) };
  }

  if (userState.user?.needsOnboarding) {
    return { action: "redirect", to: onboardingDestination };
  }

  if (userState.isError) {
    if (userState.errorStatus === 401) {
      return { action: "redirect", to: onboardingDestination };
    }
    return { action: "error" };
  }

  if (!userState.user) {
    return { action: "redirect", to: onboardingDestination };
  }

  return { action: "render" };
}
